import {z} from 'zod';

import {
  BaseVehicleResponseSchema,
  Co2EmissionClassEnumSchema,
  ConditionEnumSchema,
  DimensionsV2ResponseBodySchema,
  FuelTypeEnumSchema,
  HybridTypeEnumSchema,
  VehicleTypeEnumSchema,
} from '@dms/api/vehicle';
import {UmweltplaketteEnum} from '@dms/shared';

// Regex for field transformations
const NOT_ALPHANUMERIC_REGEX_FILTER = /[^A-Z0-9]+/g;

export const BaseVehicleValidationSchema = BaseVehicleResponseSchema.omit({type: true}).extend({
  id: z.string().nullable().optional(),

  title: z.string().max(100),
  type: z.array(VehicleTypeEnumSchema),
  condition: z.array(ConditionEnumSchema).optional(),
  bodyStyle: z.array(z.string()).optional(),
  fuelType: z.array(FuelTypeEnumSchema).optional(),
  secondaryFuelType: z.array(FuelTypeEnumSchema).optional(),
  hybridType: z.array(HybridTypeEnumSchema).optional(),
  transmission: z.array(z.string()).optional(),
  drive: z.array(z.string()).optional(),
  exteriorColorSpecification: z.array(z.string()).optional(),
  serviceBookType: z.array(z.string()).optional(),
  serviceBookState: z.array(z.string()).optional(),
  features: z.array(z.string()).optional().nullable(),
  doorCountCategory: z.array(z.string()).optional(),
  seatCount: z.array(z.union([z.string(), z.number()])).optional(),
  axleCount: z.array(z.string()).optional(),
  interiorMaterial: z.array(z.string()).optional(),

  // Overriding fields from BaseVehicleResponseSchema to handle form data types
  dimensions: DimensionsV2ResponseBodySchema.extend({
    width: z.coerce.string().nullable().optional(),
    height: z.coerce.string().nullable().optional(),
    length: z.coerce.string().nullable().optional(),
  })
    .nullable()
    .optional(),

  dimensionsLoadingSpace: DimensionsV2ResponseBodySchema.extend({
    width: z.coerce.string().nullable().optional(),
    height: z.coerce.string().nullable().optional(),
    length: z.coerce.string().nullable().optional(),
  })
    .nullable()
    .optional(),

  loadingArea: z.coerce.string().nullable().optional(),

  lengthCategory: z.string().nullable().optional(),
  heightCategory: z.string().nullable().optional(),

  maxTorque: z.coerce.string().nullable().optional(),
  // NEDC consumption fields
  fuelConsumptionCombined: z.coerce.string().nullable().optional(),
  fuelConsumptionExtraUrban: z.coerce.string().nullable().optional(),
  fuelConsumptionUrban: z.coerce.string().nullable().optional(),
  // WLTP consumption fields (liquid/gas)
  fuelConsumptionWltpCombined: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpCityCenter: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpOutskirts: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpCountryRoads: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpHighway: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpWeighted: z.coerce.string().nullable().optional(),
  // WLTP consumption fields (electric)
  fuelConsumptionWltpCombinedKwh: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpCityCenterKwh: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpOutskirtsKwh: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpCountryRoadsKwh: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpHighwayKwh: z.coerce.string().nullable().optional(),
  fuelConsumptionWltpWeightedCombinedKwh: z.coerce.string().nullable().optional(),
  carbonDioxideEmission: z.coerce.string().nullable().optional(),
  carbonDioxideEmissionDischarged: z.coerce.string().nullable().optional(),
  carbonDioxideEmissionWeightedCombined: z.coerce.string().nullable().optional(),
  emissionClass: z.coerce.string().nullable().optional(),
  carbonDioxideEmissionClass: Co2EmissionClassEnumSchema.nullable().optional(),
  carbonDioxideEmissionClassDischarged: Co2EmissionClassEnumSchema.nullable().optional(),
  carbonDioxideEmissionClassWeightedCombined: Co2EmissionClassEnumSchema.nullable().optional(),

  exteriorColor: z.string().nullable().optional(),
  interiorColor: z.string().nullable().optional(),
  interiorColorName: z.string().nullable().optional(),

  registrationPlate: z
    .string()
    .toUpperCase()
    .transform((val) => val.replace(NOT_ALPHANUMERIC_REGEX_FILTER, ''))
    .nullable()
    .optional(),

  // Vehicle registration fields with built-in Zod transforms
  vehicleRegistrationNumber: z
    .string()
    .max(17)
    .toUpperCase()
    .transform((val) => val.replace(NOT_ALPHANUMERIC_REGEX_FILTER, ''))
    .nullable()
    .optional(),
  registrationCertificateNumber: z
    .string()
    .max(17)
    .toUpperCase()
    .transform((val) => val.replace(NOT_ALPHANUMERIC_REGEX_FILTER, ''))
    .nullable()
    .optional(),

  // German-specific fields with validation and transforms
  hsn: z.string().length(4).nullable().optional(),
  tsn: z.string().min(3).max(7).toUpperCase().nullable().optional(),
  umweltplakette: z.nativeEnum(UmweltplaketteEnum).nullable().optional(),

  // These fields are part of the UI state but not the API response.
  sessionId: z.string().nullable().optional(),
  formMake: z.string(),
  formModelFamily: z.string(),
  manufacturedOnYear: z.string().nullable().optional(),
  manufacturedOnMonth: z.string().nullable().optional(),
  modelYear: z.string().nullable().optional(),
  unknownMakes: z.array(z.object({value: z.string(), label: z.string()})).optional(),
  unknownModels: z.array(z.object({value: z.string(), label: z.string()})).optional(),
  auditData: z.record(z.unknown()).optional(),
  auditAssets: z.record(z.unknown()).optional(),

  // Additional note
  additionalNote: z.string().nullable().optional(),

  // Integer fields that should be sent as numbers to the API
  power: z.number().int().nullable().optional(),
  engineVolume: z.number().int().nullable().optional(),
  weight: z.number().int().nullable().optional(),
  operatingWeight: z.number().int().nullable().optional(),
  loadingCapacity: z.number().int().nullable().optional(),
  trunkSpace: z.number().int().nullable().optional(),
  wheelBase: z.number().int().nullable().optional(),
  gearCount: z.number().int().nullable().optional(),
  ownerCount: z.number().int().nullable().optional(),
  primaryKeyCount: z.number().int().nullable().optional(),
  cylinderCount: z.number().int().nullable().optional(),

  // Electric Vehicle Battery - nested structure for electric/hybrid vehicles
  electricVehicleBattery: z
    .object({
      chargingAC: z.coerce.string().nullable().optional(),
      chargingDC: z.coerce.string().nullable().optional(),
      batteryCapacityWh: z.coerce.string().nullable().optional(),
      batteryCapacityAh: z.coerce.string().nullable().optional(),
      batteryVoltage: z.coerce.string().nullable().optional(),
    })
    .nullable()
    .optional(),

  batteryCapacityWh: z.number().int().nullable().optional(),
  batteryCapacityAh: z.number().int().nullable().optional(),
  batteryVoltage: z.number().int().nullable().optional(),
  batteryCount: z.number().int().nullable().optional(),
  chargingAC: z.number().int().nullable().optional(),
  chargingDC: z.number().int().nullable().optional(),
  chargingTimeAC: z.number().nullable().optional(),
  chargingTimeDC: z.number().int().nullable().optional(),
  electricRange: z.number().int().nullable().optional(),
  batteryMileageLimit: z.number().int().nullable().optional(),
  batteryWarrantyUntil: z.coerce.string().nullable().optional(),
  batteryOwnershipType: z.coerce.string().nullable().optional(),
  hasRangeExtender: z.boolean().nullable().optional(),
  maximalPower: z.number().int().nullable().optional(),

  // Battery health fields
  batteryCondition: z.coerce.string().nullable().optional(),
  batteryConditionMeasuredAt: z.coerce.string().nullable().optional(),
  actualBatteryRange: z.number().int().nullable().optional(),
  batteryCertificate: z.boolean().nullable().optional(),
  batteryCertificateDocument: z
    .object({
      fileId: z.string(),
      fileName: z.string(),
      fileUri: z.string(),
      fileType: z.string(),
    })
    .nullable()
    .optional(),
});

export type BaseVehicleValidationSchemaType = z.infer<typeof BaseVehicleValidationSchema>;
